import {
  Component, OnInit, OnDestroy,
  inject, signal, computed,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ReactiveFormsModule,
  FormGroup, FormControl, Validators,
} from '@angular/forms';
import { LucideAngularModule, LucideIconProvider, LUCIDE_ICONS, icons } from 'lucide-angular';
import { combineLatest, Subject, takeUntil } from 'rxjs';
import { FeaturesAgentService } from '../../../services/features-agent.service';
import { Feature, FeatureCategory, GroupedFeatures } from '../../../models/feature.model';
import { SPACE_CATEGORIES, SPACE_CATEGORY_LABELS } from '../../../models/asset.model';

@Component({
  selector: 'app-admin-amenities',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, LucideAngularModule],
  providers: [{ provide: LUCIDE_ICONS, multi: true, useValue: new LucideIconProvider(icons) }],
  templateUrl: './admin-amenities.component.html',
})
export class AdminAmenitiesComponent implements OnInit, OnDestroy {
  private featuresAgent = inject(FeaturesAgentService);
  private destroy$      = new Subject<void>();

  // ── List state ──────────────────────────────────────────────────────────────
  readonly loading    = signal(true);
  readonly saving     = signal(false);
  readonly categories = signal<FeatureCategory[]>([]);
  readonly features   = signal<Feature[]>([]);

  readonly grouped = computed<GroupedFeatures[]>(() =>
    this.categories()
      .slice()
      .sort((a, b) => a.order - b.order)
      .map(cat => ({
        category: cat,
        features: this.features()
          .filter(f => f.categoryId === cat.id)
          .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)),
      }))
      .filter(g => g.features.length > 0),
  );

  readonly reordering = signal(false);

  // ── Form / edit state ───────────────────────────────────────────────────────
  readonly editingId         = signal<string | 'new' | null>(null);
  readonly allowedCategories = signal<string[]>(['global']);
  readonly saveError         = signal<string | null>(null);
  readonly deleteId          = signal<string | null>(null);

  readonly form = new FormGroup({
    label:      new FormControl<string>('',     { nonNullable: true, validators: [Validators.required, Validators.minLength(2)] }),
    icon:       new FormControl<string>('star', { nonNullable: true, validators: [Validators.required] }),
    categoryId: new FormControl<string>('',     { nonNullable: true, validators: [Validators.required] }),
    isActive:   new FormControl<boolean>(true,  { nonNullable: true }),
    sortOrder:  new FormControl<number>(0,      { nonNullable: true }),
  });

  // ── Exposed constants ───────────────────────────────────────────────────────
  readonly SPACE_CATEGORIES      = SPACE_CATEGORIES;
  readonly SPACE_CATEGORY_LABELS = SPACE_CATEGORY_LABELS;

  // ── Lifecycle ───────────────────────────────────────────────────────────────

  ngOnInit(): void {
    combineLatest([
      this.featuresAgent.watchAllCategories(),
      this.featuresAgent.watchAllFeatures(),
    ])
    .pipe(takeUntil(this.destroy$))
    .subscribe(([cats, feats]) => {
      this.categories.set(cats);
      this.features.set(feats);
      this.loading.set(false);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── CRUD ────────────────────────────────────────────────────────────────────

  onCreateAmenity(): void {
    this.editingId.set('new');
    this.allowedCategories.set(['global']);
    this.saveError.set(null);
    const defaultCatId = this.categories()[0]?.id ?? '';
    const groupLen = this.features().filter(f => f.categoryId === defaultCatId).length;
    this.form.reset({
      label:      '',
      icon:       'star',
      categoryId: defaultCatId,
      isActive:   true,
      sortOrder:  groupLen,
    });
    this._scrollToForm();
  }

  onEditAmenity(f: Feature): void {
    this.editingId.set(f.id);
    this.allowedCategories.set([...(f.allowedCategories ?? ['global'])]);
    this.saveError.set(null);
    this.form.reset({
      label:      f.label,
      icon:       f.icon,
      categoryId: f.categoryId,
      isActive:   f.isActive ?? true,
      sortOrder:  f.sortOrder ?? 0,
    });
    this._scrollToForm();
  }

  cancelForm(): void {
    this.editingId.set(null);
    this.saveError.set(null);
    this.form.reset();
  }

  async saveForm(): Promise<void> {
    this.form.markAllAsTouched();
    if (this.form.invalid) return;

    this.saving.set(true);
    this.saveError.set(null);
    try {
      const v = this.form.getRawValue();
      const payload: Omit<Feature, 'id'> = {
        label:             v.label.trim(),
        icon:              v.icon.trim(),
        categoryId:        v.categoryId,
        allowedCategories: this.allowedCategories(),
        isActive:          v.isActive,
        sortOrder:         v.sortOrder,
      };
      const id = this.editingId();
      if (id === 'new') {
        await this.featuresAgent.createFeature(payload);
      } else if (id) {
        await this.featuresAgent.updateFeature(id, payload);
      }
      this.cancelForm();
    } catch {
      this.saveError.set('Error al guardar. Inténtalo de nuevo.');
    } finally {
      this.saving.set(false);
    }
  }

  async onToggleActiveStatus(f: Feature): Promise<void> {
    if (!f.id) return;
    await this.featuresAgent.updateFeature(f.id, { isActive: !f.isActive });
  }

  confirmDelete(id: string): void { this.deleteId.set(id);   }
  cancelDelete():            void { this.deleteId.set(null);  }

  async executeDelete(): Promise<void> {
    const id = this.deleteId();
    if (!id) return;
    this.saving.set(true);
    try {
      await this.featuresAgent.deleteFeature(id);
      this.deleteId.set(null);
      if (this.editingId() === id) this.cancelForm();
    } finally {
      this.saving.set(false);
    }
  }

  // ── Reorder ─────────────────────────────────────────────────────────────────

  async moveFeatureUp(f: Feature): Promise<void> {
    const group = this._sortedGroup(f.categoryId);
    const idx = group.findIndex(x => x.id === f.id);
    if (idx <= 0 || this.reordering()) return;
    await this._swapAndPersist(group, idx, idx - 1);
  }

  async moveFeatureDown(f: Feature): Promise<void> {
    const group = this._sortedGroup(f.categoryId);
    const idx = group.findIndex(x => x.id === f.id);
    if (idx >= group.length - 1 || this.reordering()) return;
    await this._swapAndPersist(group, idx, idx + 1);
  }

  isFirstInGroup(f: Feature): boolean {
    return this._sortedGroup(f.categoryId)[0]?.id === f.id;
  }

  isLastInGroup(f: Feature): boolean {
    const g = this._sortedGroup(f.categoryId);
    return g[g.length - 1]?.id === f.id;
  }

  // ── allowedCategories helpers ───────────────────────────────────────────────

  toggleAllowedCategory(cat: string): void {
    if (cat === 'global') {
      this.allowedCategories.set(['global']);
      return;
    }
    const current = this.allowedCategories().filter(c => c !== 'global');
    const updated = current.includes(cat)
      ? current.filter(c => c !== cat)
      : [...current, cat];
    this.allowedCategories.set(updated.length ? updated : ['global']);
  }

  isAllowedSelected(cat: string): boolean {
    const a = this.allowedCategories();
    if (cat === 'global') return a.includes('global') || a.length === 0;
    return a.includes(cat) && !a.includes('global');
  }

  // ── Display helpers ─────────────────────────────────────────────────────────

  allowedPills(f: Feature): string[] {
    const a = f.allowedCategories ?? [];
    return (!a.length || a.includes('global')) ? ['global'] : a;
  }

  pillLabel(cat: string): string {
    if (cat === 'global') return 'Global';
    return (SPACE_CATEGORY_LABELS as Record<string, string>)[cat] ?? cat;
  }

  hasError(field: string): boolean {
    const c = this.form.get(field);
    return !!(c?.invalid && c?.touched);
  }

  iconPreview(): string {
    return this.form.controls.icon.value?.trim() || 'star';
  }

  categoryName(id: string): string {
    return this.categories().find(c => c.id === id)?.label ?? id;
  }

  // ── Private ─────────────────────────────────────────────────────────────────

  private _sortedGroup(categoryId: string): Feature[] {
    return this.features()
      .filter(x => x.categoryId === categoryId)
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  }

  private async _swapAndPersist(sorted: Feature[], i: number, j: number): Promise<void> {
    this.reordering.set(true);
    try {
      const arr = [...sorted];
      [arr[i], arr[j]] = [arr[j], arr[i]];
      // Re-assign sequential sortOrder (0, 1, 2, …) and persist all
      await Promise.all(
        arr.map((f, idx) =>
          f.id ? this.featuresAgent.updateFeature(f.id, { sortOrder: idx }) : Promise.resolve(),
        ),
      );
    } finally {
      this.reordering.set(false);
    }
  }

  private _scrollToForm(): void {
    setTimeout(() =>
      document.getElementById('amenity-form')
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
      80,
    );
  }
}
